import { Star, Trash } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import Accordion from 'src/components/Accordion';
import { Button } from 'src/components/Button';
import { Rating } from 'src/components/Rating';
import Table from 'src/components/Table';
import { Textarea } from 'src/components/Textarea';
import { TranscriptDisplay } from 'src/components/TranscriptDisplay';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Call } from 'src/interfaces/call.interface';
import { friendlyDateFormatter } from 'src/utils/date';
import { parseTranscript } from 'src/utils/transcript';

const headers = [
  {
    key: 'created_at',
    label: 'Created At',
    width: '15%',
    disableSorting: true,
  },
  {
    key: 'comment',
    label: 'Comment',
    width: '65%',
    disableSorting: true,
  },
  {
    key: 'overall_rating',
    label: 'Rating',
    width: '6rem',
    disableSorting: true,
  },
  { key: 'actions', label: 'Actions', width: '10%', disableSorting: true },
];

type CallFeedbackSidebarProps = {
  call: Call;
  isOpen: boolean;
  onClose: () => void;
};

export const CallFeedbackSidebar = ({ call }: CallFeedbackSidebarProps) => {
  const { get, post, remove } = useAuthedRequest();
  const [feedback, setFeedback] = useState<string>('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [overallRating, setOverallRating] = useState(3);

  const transcriptMessages = parseTranscript(call.transcript || '');

  useEffect(() => {
    if (call && call.id) {
      fetchData(currentPage, size);
      resetFeedback();
    }
  }, [call]);

  const fetchData = async (page: number, perPage: number) => {
    setLoading(true);
    const query = new URLSearchParams({
      page: String(page),
      size: String(perPage),
    });
    const response = await get(
      `/calls/${call.id}/feedbacks?${query.toString()}`,
    );

    setRows(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      response.items.map((item: any) => ({
        created_at: friendlyDateFormatter(new Date(item.created_at)),
        comment: item.comment,
        overall_rating: (
          <div className="flex items-center gap-1">
            <span className="mt-1">{item.overall_rating}</span>
            <Star size={16} weight="fill" className="text-primary" />
          </div>
        ),
        actions: (
          <Button
            variant="text"
            color="error"
            onClick={() => {
              handleDeleteFeedback(item.id);
            }}
          >
            <Trash size={16} />
          </Button>
        ),
      })),
    );
    setTotalItems(response.total);
    setLoading(false);
  };

  const resetFeedback = useCallback(() => {
    setFeedback('');
    setOverallRating(3);
  }, []);

  const handleAddFeedback = useCallback(async () => {
    if (!call || !call.id || !feedback) return;
    setLoading(true);
    await post(`/calls/${call.id}/feedbacks`, {
      execution_id: call.id,
      comment: feedback,
      overall_rating: overallRating,
      tags: [],
    });
    await fetchData(currentPage, size);
    resetFeedback();
    setLoading(false);
  }, [call, feedback, overallRating]);

  const handleDeleteFeedback = useCallback(
    async (id: string) => {
      if (!id) return;
      setLoading(true);
      await remove(`/calls/${call.id}/feedbacks/${id}`);
      await fetchData(currentPage, size);
      setLoading(false);
    },
    [currentPage, size],
  );

  const handlePageChange = async (page: number, perPage: number) => {
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setSize(perPage);
    setCurrentPage(1);
    fetchData(1, perPage);
  };

  return (
    <div className="flex flex-col gap-6">
      <Accordion title="Feedback" defaultOpen={true} className="border-none">
        <div className="flex-1 flex flex-col">
          <div className="flex flex-col gap-2">
            <Rating
              value={overallRating}
              onChange={(_, value) => {
                setOverallRating(value || 0);
              }}
            />
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
            <div className="flex gap-2 justify-end mb-4">
              <Button
                variant="contained"
                color="default"
                disabled={loading}
                onClick={resetFeedback}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={loading || !feedback || overallRating === 0}
                onClick={handleAddFeedback}
              >
                Save
              </Button>
            </div>
          </div>
          <Table
            headers={headers}
            rows={rows}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={() => {}}
            loading={loading}
            setSize={handleSizeChange}
            defaultSize={size}
          />
        </div>
      </Accordion>

      <Accordion title="Transcript" defaultOpen={true} className="border-none">
        <div className="flex-1 flex flex-col">
          <TranscriptDisplay
            messages={transcriptMessages}
            rawTranscript={call.transcript}
            hipaaCompliant={call.hipaa_compliant}
          />
        </div>
      </Accordion>
    </div>
  );
};
